import { fetchApi } from "@/lib/api";

export interface Cohort {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  locked: boolean;
  twoWaySms: boolean;
  sqlStatement: string;
  priority: number;
  version: number;
  stepCount: number;
  created_at: string;
  updated_at: string;
}

export interface Step {
  id: string;
  cohortId: string;
  isRoot: boolean;
  parentId: string | null;
  childId: string | null;
  messages: string[];
  sendStartTime: string;
  sendEndTime: string;
  minGapHours: number;
  channels: string[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface SmsSettings {
  [key: string]: string;
}

export interface AuditTraceItem {
  id: string;
  user_id: string;
  cohort_id: string;
  cohort_name: string;
  notification_id: string;
  scheduled_at: string;
  sent_at: string | null;
  message: string;
  channels: string[];
}

export interface AuditTraceResult {
  items: AuditTraceItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Cohorts
export const listCohorts = async () => {
  const { data } = await fetchApi<{ cohorts: Cohort[] }>({
    url: "/v2/sms/cohorts",
    method: "GET",
  });
  return data.cohorts;
};

export const createCohort = async (payload: { name: string; description?: string }) => {
  const { data } = await fetchApi<Cohort>({
    url: "/v2/sms/cohorts",
    method: "POST",
    data: payload,
  });
  return data;
};

export const updateCohort = async (
  id: string,
  payload: Partial<{ name: string; description: string; enabled: boolean; sqlStatement: string; twoWaySms: boolean }>,
) => {
  const { data } = await fetchApi<Cohort>({
    url: `/v2/sms/cohorts/${id}`,
    method: "PATCH",
    data: payload,
  });
  return data;
};

export const reorderCohorts = async (orderedIds: string[]) => {
  const { data } = await fetchApi<{ cohorts: Cohort[] }>({
    url: "/v2/sms/cohorts/reorder",
    method: "PATCH",
    data: { orderedIds },
  });
  return data.cohorts;
};

export const deleteCohort = async (id: string) => {
  await fetchApi({ url: `/v2/sms/cohorts/${id}`, method: "DELETE" });
};

export const unlockCohort = async (id: string) => {
  const { data } = await fetchApi<Cohort>({
    url: `/v2/sms/cohorts/${id}/unlock`,
    method: "POST",
  });
  return data;
};

// Steps
export const listSteps = async (cohortId: string) => {
  const { data } = await fetchApi<{ steps: Step[] }>({
    url: `/v2/sms/cohorts/${cohortId}/steps`,
    method: "GET",
  });
  return data.steps;
};

export const addStep = async (
  cohortId: string,
  payload: {
    messages: string[];
    sendStartTime?: string;
    sendEndTime?: string;
    minGapHours?: number;
    channels?: string[];
    insertAfterStepId?: string | null;
  },
) => {
  const { data } = await fetchApi<Step>({
    url: `/v2/sms/cohorts/${cohortId}/steps`,
    method: "POST",
    data: payload,
  });
  return data;
};

export const updateStep = async (
  id: string,
  payload: Partial<{
    messages: string[];
    sendStartTime: string;
    sendEndTime: string;
    minGapHours: number;
    channels: string[];
    enabled: boolean;
  }>,
) => {
  const { data } = await fetchApi<Step>({
    url: `/v2/sms/steps/${id}`,
    method: "PATCH",
    data: payload,
  });
  return data;
};

export const deleteStep = async (id: string) => {
  await fetchApi({ url: `/v2/sms/steps/${id}`, method: "DELETE" });
};

// Settings
export const getSettings = async () => {
  const { data } = await fetchApi<{ settings: SmsSettings }>({
    url: "/v2/sms/settings",
    method: "GET",
  });
  return data.settings;
};

export const updateSettings = async (settings: SmsSettings) => {
  const { data } = await fetchApi<{ settings: SmsSettings }>({
    url: "/v2/sms/settings",
    method: "PATCH",
    data: settings,
  });
  return data.settings;
};

// Observability — per-cohort stats
export interface CohortStatsItem {
  cohortId: string;
  cohortName: string;
  totalScheduled: number;
  totalSent: number;
  totalPending: number;
  activeUsers: number;
}

export const getCohortStats = async (params?: {
  startDate?: string;
  endDate?: string;
}) => {
  const { data } = await fetchApi<{ stats: CohortStatsItem[] }>({
    url: "/v2/sms/observability/cohort-stats",
    method: "GET",
    params,
  });
  return data.stats;
};

// Observability — cohort size over time
export interface CohortSizePoint {
  date: string;
  cohortId: string;
  cohortName: string;
  userCount: number;
}

export const getCohortSizeHistory = async (params?: { days?: number }) => {
  const { data } = await fetchApi<{ points: CohortSizePoint[] }>({
    url: "/v2/sms/observability/cohort-size-history",
    method: "GET",
    params,
  });
  return data.points;
};

// Observability — audit trace
export const getAuditTrace = async (params?: {
  userId?: string;
  cohortId?: string;
  startDate?: string;
  endDate?: string;
  status?: "sent" | "pending" | "all";
  page?: number;
  limit?: number;
}) => {
  const { data } = await fetchApi<AuditTraceResult>({
    url: "/v2/sms/observability",
    method: "GET",
    params,
  });
  return data;
};